import type { SlashCommand } from "./types"
import type { EditorConfig, ToolbarOption } from "./types";

export const editorProps: EditorConfig = {
    isShortCuts: true,
    isHtmlButton: false,
    isDisabled: false,
    mentionList: [],
    placeholder: "Write something amazing...",
    toolbarOptions: [],
    toolbarType: "Advanced",
    isSlashCommand: true,
    isMention: false,
    isDragDrop: false,
    isEmojiPicker: true,
    pasteImageType: "pasteImage",
    imageUploadData: {
        route: "/api/upload",
        params: {},
        dataKey: "image",
        method: "post",
        imageUrlKey: "image_url",
    },
    allowedFileTypes: [],
    isTableContent: false,
    textLimit: {
        display: false,
        limit: 500,
        type: 'character',
    },
    editorType: 'normal'
}
export const slashCommands: SlashCommand[] =
    [
        {
            key: "heading",
            title: "Heading 1",
            iconClass: "mdi-icon-tip-tap mdi-h1",
            command: ({ editor, range }) =>
                editor.chain().focus().deleteRange(range).setNode("heading", { level: 1 }).run(),
        },
        {
            key: "heading",
            title: "Heading 2",
            iconClass: "mdi-icon-tip-tap mdi-h2",
            command: ({ editor, range }) =>
                editor.chain().focus().deleteRange(range).setNode("heading", { level: 2 }).run(),
        },
        {
            key: "heading",
            title: "Heading 3",
            iconClass: "mdi-icon-tip-tap mdi-h3",
            command: ({ editor, range }) =>
                editor.chain().focus().deleteRange(range).setNode("heading", { level: 3 }).run(),
        },
        {
            key: "heading",
            title: "Heading 4",
            iconClass: "mdi-icon-tip-tap mdi-h4",
            command: ({ editor, range }) =>
                editor.chain().focus().deleteRange(range).setNode("heading", { level: 4 }).run(),
        },
        {
            key: "list",
            title: "Numbered List",
            iconClass: "mdi-icon-tip-tap mdi-list-number",
            command: ({ editor, range }) =>
                editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
        },
        {
            key: "list",
            title: "Bullet List",
            iconClass: "mdi-icon-tip-tap mdi-list-bullet",
            command: ({ editor, range }) =>
                editor.chain().focus().deleteRange(range).toggleBulletList().run(),
        },
        {
            key: "list",
            title: "Task List",
            iconClass: "mdi-icon-tip-tap mdi-list-check",
            command: ({ editor, range }) =>
                editor.chain().focus().deleteRange(range).toggleTaskList().run(),
        },
        {
            key: "codeBlock",
            title: "Code Block",
            iconClass: "mdi-icon-tip-tap mdi-code-block",
            command: ({ editor, range }) =>
                editor.chain().focus().deleteRange(range).toggleCodeBlock().run(),
        },
        {
            key: "divider",
            title: "Divider",
            iconClass: "mdi-icon-tip-tap mdi-horizontal-line",
            command: ({ editor, range }) =>
                editor.chain().focus().deleteRange(range).setHorizontalRule().run(),
        },
        // {
        //     key: "aiGenerate",
        //     title: "Ask AI",
        //     iconClass: "mdi-icon-tip-tap mingcute:ai-fill",
        //     command: ({ editor, range }) =>
        //         // Chain multiple commands safely
        //         editor
        //             .chain()
        //             .focus()
        //             .deleteRange(range)
        //             .insertContent({ type: "aiNode" })
        //             .run()
        // },
    ]

export const defaultOptions: ToolbarOption[] = ([
    "bold",
    "italic",
    "strike",
    "paragraph",
    "heading",
    "list",
    "link",
    "highlight",
    "fontColor",
    "code",
    "table",
    "codeBlock",
    "blockquote",
    "horizontalRule",
    "undo",
    "redo",
    "align",
    "fontSize",
    "fontFamily",
    "aiGenerate",
    "math",
    "youtube",
    "subScript",
    "supScript",
    "clearFormat",
    "signAdd"
]);

// export const filterCommands = (options: ToolbarOption) => {
//     const fc = slashCommands.filter((cmd) => options.includes(cmd.key));
//     return fc;
// }

export const filterCommands = (options: ToolbarOption[]) => {
    // return only matching slash commands
    return slashCommands.filter(cmd => options.includes(cmd.key as ToolbarOption));
}

export const getClassName = (fontFamily: string) => {
    switch (fontFamily) {
        case 'TipTapFiraCode':
        case 'Fira Code':
            return 'typeface-fira'
        case 'TipTapInter':
        case 'Inter':
            return 'typeface-inter'
        case 'TipTapManrope':
        case 'Manrope':
            return 'typeface-manrope'
        case 'TipTapMerriweather':
        case 'Merriweather':
            return 'typeface-merriweather'
        case 'TipTapMontserrat':
        case 'Montserrat':
            return 'typeface-montserrat'
        case 'TipTapNunito':
        case 'Nunito':
            return 'typeface-nunito'
        case 'TipTapPlayfairDisplay':
        case 'Playfair Display':
            return 'typeface-playfair'
        case 'TipTapPoppins':
        case 'Poppins':
            return 'typeface-poppins'
        case 'TipTapRoboto':
        case 'Roboto':
            return 'typeface-roboto'
        case 'TipTapSourceCodePro':
        case 'Source Code Pro':
            return 'typeface-sourcecode'
        default:
            return ''
    }
}


export const aiActions = [
    { name: "Translate", ask_ai: "Translate this text to English", type: "inbuilt", icon: "mdi-translate" },
    { name: "Blog Post", ask_ai: "Generate a blog post on a given topic", type: "inbuilt", icon: "mdi-notebook-outline" },
    { name: "Product Description", ask_ai: "Create a product description for a given item", type: "inbuilt", icon: "mdi-tag-text-outline" },
    { name: "Social Media Post", ask_ai: "Craft a social media post for a specific platform", type: "inbuilt", icon: "mdi-share-variant-outline" },
    { name: "Email Draft", ask_ai: "Compose an email for a particular purpose", type: "inbuilt", icon: "mdi-email-edit-outline" },
    { name: "Ad Copy", ask_ai: "Write ad copy for a marketing campaign", type: "inbuilt", icon: "mdi-bullhorn-outline" },
    { name: "Story", ask_ai: "Generate a short story based on a prompt", type: "inbuilt", icon: "mdi-book-open-page-variant-outline" },
    { name: "Poem", ask_ai: "Create a poem on a specific theme or subject", type: "inbuilt", icon: "mdi-feather" },
    { name: "Recipe", ask_ai: "Generate a recipe based on given ingredients", type: "inbuilt", icon: "mdi-silverware-fork-knife" },
    { name: "Code Snippet", ask_ai: "Write a code snippet to accomplish a specific task", type: "inbuilt", icon: "mdi-code-braces" },
    { name: "Idea Generation", ask_ai: "Brainstorm ideas for a project or topic", type: "inbuilt", icon: "mdi-lightbulb-on-outline" },
    { name: "Fix Grammar", ask_ai: "Fix spelling & grammar", type: "inbuilt", icon: "mdi-spellcheck" },
    { name: "Extend Text", ask_ai: "Extend text", type: "inbuilt", icon: "mdi-arrow-expand" },
    { name: "Reduce Text", ask_ai: "Reduce text", type: "inbuilt", icon: "mdi-arrow-collapse" },
    { name: "Simplify", ask_ai: "Simplify text", type: "inbuilt", icon: "mdi-lightbulb-outline" },
    { name: "Emojify", ask_ai: "Emojify", type: "inbuilt", icon: "mdi-emoticon-happy-outline" },
    { name: "Formal Write", ask_ai: "Rewrite in a formal/professional tone", type: "inbuilt", icon: "mdi-briefcase-outline" },
    { name: "Casual Write", ask_ai: "Rewrite in a casual/friendly tone", type: "inbuilt", icon: "mdi-account-voice" },
    { name: "Bullet Points", ask_ai: "Convert this text into bullet points", type: "inbuilt", icon: "mdi-format-list-bulleted" },
    { name: "Summarize", ask_ai: "Summarize this text in 2-3 sentences", type: "inbuilt", icon: "mdi-file-document-outline" },
    { name: "Headline", ask_ai: "Generate a catchy headline for this text", type: "inbuilt", icon: "mdi-format-title" },
    { name: "Questions", ask_ai: "Generate questions based on this text", type: "inbuilt", icon: "mdi-comment-question-outline" },
    { name: "Explain Like 5", ask_ai: "Explain this text like I'm 5 years old", type: "inbuilt", icon: "mdi-baby-face-outline" },
    { name: "Code Comment", ask_ai: "Add comments to this code snippet", type: "inbuilt", icon: "mdi-comment-text-outline" },
    { name: "Code Optimize", ask_ai: "Optimize and clean up this code", type: "inbuilt", icon: "mdi-code-tags" },
];


export const fontMaps: Record<string, string> = {
    'Fira Code': 'typeface-fira',
    'Inter': 'typeface-inter',
    'Manrope': 'typeface-manrope',
    'Merriweather': 'typeface-merriweather',
    'Montserrat': 'typeface-montserrat',
    'Nunito': 'typeface-nunito',
    'Playfair Display': 'typeface-playfair',
    'Poppins': 'typeface-poppins',
    'Roboto': 'typeface-roboto',
    'Source Code Pro': 'typeface-sourcecode',
}

export const setFontColor = (bgColor: string) => {
    if (!bgColor) return "white";
    const color = bgColor.charAt(0) === "#" ? bgColor.substring(1, 7) : bgColor;
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);

    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 155 ? "black" : "white";
};